import { Button, Drawer, Input, Prompt, Select, Switch, Text, Textarea, toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Field } from "@/components/form"
import { Section } from "@/components/ui"
import { formatDate } from "@/lib/format"
import { errorMessage } from "@/lib/sdk"
import {
  MODE_LABEL,
  useClearGmailOAuthClient,
  useConnectGmail,
  useDisconnectGmail,
  useGmail,
  useGmailOptions,
  useSaveGmailOAuthClient,
  useStartGmailOAuth,
  useSyncGmail,
  useTestGmail,
  type GmailMode,
  type GmailOAuthInfo,
  type GmailOptions,
  type GmailStatus,
} from "@/lib/gmail"
import { Badge, Banner, DefinitionList } from "@/terabizz"

const MODE_HELP: Record<GmailMode, string> = {
  off: "Nothing is read from or sent through Gmail. Messages shows contact-form messages only, and replies go from the store's email service as before.",
  import: "Emails to the mailbox show in Messages (and replies sent from Gmail mark them replied), but nothing is sent from here: answer emails in Gmail. Use this to check everything before switching to full.",
  full: "Emails show in Messages and the Reply box sends through Gmail as the mailbox, in the same thread, so replies are in Gmail's Sent folder and land in the customer's inbox.",
}

/** The Workspace admin's steps (from the backend, so the docs and the page never disagree). */
export function GmailSetupSteps({ s, open = false }: { s: Pick<GmailStatus, "setup_steps" | "scopes" | "client_id">; open?: boolean }) {
  return (
    <details className="rounded-md border border-ui-border-base px-3 py-2 text-[13px]" open={open}>
      <summary className="cursor-pointer font-medium">Setup steps for the Google Workspace admin (service account)</summary>
      <ol className="mt-2 flex list-decimal flex-col gap-1.5 pl-5 text-ui-fg-subtle">
        {s.setup_steps.map((x, i) => (
          <li key={i} className="break-words">{x}</li>
        ))}
      </ol>
      {s.client_id ? (
        <p className="mt-2 break-all text-[12px]">
          This key's client ID: <code className="select-all">{s.client_id}</code> · scopes: <code className="select-all">{s.scopes.join(",")}</code>
        </p>
      ) : null}
    </details>
  )
}

const EMAIL_RE = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/

/** "Sign in with Google": sends the browser to Google; it comes back to this page (?gmail_oauth=…). */
function useGoogleSignIn() {
  const start = useStartGmailOAuth()
  const go = (b: { mailbox?: string; display_name?: string }) =>
    start.mutate(b, {
      onSuccess: (r) => window.location.assign(r.url),
      onError: (e) => toast.error(errorMessage(e)),
    })
  return { go, pending: start.isPending || start.isSuccess }
}

/** The Google Cloud steps for Sign in with Google, with the exact redirect URI to register. */
export function GmailOAuthSteps({ o, open = false }: { o: GmailOAuthInfo; open?: boolean }) {
  return (
    <details className="rounded-md border border-ui-border-base px-3 py-2 text-[13px]" open={open}>
      <summary className="cursor-pointer font-medium">Setup steps in Google Cloud (once, by a Google Workspace admin)</summary>
      <ol className="mt-2 flex list-decimal flex-col gap-1.5 pl-5 text-ui-fg-subtle">
        {o.setup_steps.map((x, i) => (
          <li key={i} className="break-words">{x}</li>
        ))}
      </ol>
      <p className="mt-2 break-all text-[12px]">
        Redirect URI: <code className="select-all" data-testid="gmail-redirect-uri">{o.redirect_uri}</code> · scope: <code className="select-all">{o.scopes.join(" ")}</code>
      </p>
    </details>
  )
}

/** The OAuth client (ID + secret) Sign in with Google uses: from the server, or pasted here. */
function OAuthClientCard({ o, manage }: { o: GmailOAuthInfo; manage: boolean }) {
  const save = useSaveGmailOAuthClient()
  const clear = useClearGmailOAuthClient()
  const [editing, setEditing] = useState(false)
  const [id, setId] = useState("")
  const [secret, setSecret] = useState("")
  const c = o.client
  const idOk = /^[0-9]{5,40}-[a-z0-9]{8,64}\.apps\.googleusercontent\.com$/.test(id.trim())
  const secretOk = /^[A-Za-z0-9_-]{10,200}$/.test(secret.trim())
  const form = (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Client ID" hint={id && !idOk ? "It ends in .apps.googleusercontent.com." : "Google Cloud → Google Auth platform → Clients → your Web application client."}>
          <Input value={id} autoComplete="off" spellCheck={false} placeholder="1234567890-abc….apps.googleusercontent.com" onChange={(e) => setId(e.target.value)} aria-label="OAuth client ID" />
        </Field>
        <Field label="Client secret" hint="Stored encrypted and never shown again.">
          <Input type="password" value={secret} autoComplete="new-password" spellCheck={false} placeholder="GOCSPX-…" onChange={(e) => setSecret(e.target.value)} aria-label="OAuth client secret" />
        </Field>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        {c.configured ? <Button size="small" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button> : null}
        <Button
          size="small"
          variant="secondary"
          disabled={!idOk || !secretOk}
          isLoading={save.isPending}
          onClick={() =>
            save.mutate(
              { client_id: id.trim(), client_secret: secret.trim() },
              { onSuccess: () => (toast.success("OAuth client saved"), setEditing(false), setId(""), setSecret("")), onError: (e) => toast.error(errorMessage(e)) },
            )
          }
        >
          Save client
        </Button>
      </div>
    </div>
  )
  return (
    <div className="flex flex-col gap-2 rounded-md border border-ui-border-base px-3 py-3 text-[13px]" data-testid="gmail-oauth-client">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">OAuth client</span>
        {c.configured ? <Badge tone="success">{c.source === "env" ? "From the server's settings" : "Saved here"}</Badge> : <Badge tone="attention">Not set</Badge>}
      </div>
      {c.configured ? (
        <p className="break-all text-ui-fg-subtle">
          <code>{c.client_id}</code> · secret {c.secret_hint}
          {c.source === "admin" && c.updated_by ? ` · saved by ${c.updated_by}${c.updated_at ? ` ${formatDate(c.updated_at)}` : ""}` : ""}
        </p>
      ) : (
        <p className="text-ui-fg-subtle">Paste the Web application client's ID and secret from Google Cloud, or IT sets GMAIL_OAUTH_CLIENT_ID and GMAIL_OAUTH_CLIENT_SECRET on the server.</p>
      )}
      {manage && (!c.configured || editing) ? form : null}
      {manage && c.configured && !editing ? (
        <div className="flex flex-wrap justify-end gap-2">
          {c.source === "admin" ? (
            <Button size="small" variant="secondary" isLoading={clear.isPending} onClick={() => clear.mutate(undefined, { onSuccess: () => toast.success(c.env_configured ? "Pasted client removed: the server's client is used" : "Pasted client removed"), onError: (e) => toast.error(errorMessage(e)) })}>
              {c.env_configured ? "Use the server's client" : "Remove"}
            </Button>
          ) : null}
          <Button size="small" variant="secondary" onClick={() => setEditing(true)}>{c.source === "env" ? "Paste a different client" : "Change"}</Button>
        </div>
      ) : null}
    </div>
  )
}

/** Not connected: Sign in with Google as the mailbox (default), or a service-account key under "Other method". */
function SignInPanel({ s, onServiceAccount }: { s: GmailStatus; onServiceAccount: () => void }) {
  const o = s.oauth
  const [mailbox, setMailbox] = useState(o.default_mailbox)
  const [name, setName] = useState(s.display_name ?? "")
  const google = useGoogleSignIn()
  const valid = EMAIL_RE.test(mailbox.trim())
  return (
    <div className="flex flex-col gap-4 border-t border-ui-border-base px-4 py-4" data-testid="gmail-signin">
      <div className="flex flex-col gap-1">
        <Text size="small" weight="plus">Sign in with Google</Text>
        <Text size="xsmall" className="text-ui-fg-subtle">
          Sign in as the support mailbox itself. Only that one mailbox is connected (Gmail permission: read, label and send). No service-account key or domain-wide delegation is needed.
        </Text>
      </div>
      <OAuthClientCard o={o} manage />
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Mailbox" hint={mailbox && !valid ? "Enter an email address." : "Sign in as exactly this address: any other account is refused."}>
          <Input value={mailbox} onChange={(e) => setMailbox(e.target.value)} aria-label="Mailbox" placeholder="hello@yourstore.com" />
        </Field>
        <Field label="Name customers see" hint="Shown as the sender of replies.">
          <Input value={name} maxLength={80} placeholder="Tolvane Labs Support" onChange={(e) => setName(e.target.value)} aria-label="Display name" />
        </Field>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Text size="xsmall" className="max-w-[60ch] text-ui-fg-subtle">You'll be sent to Google. Choose {valid ? mailbox.trim().toLowerCase() : "the mailbox"} and allow access to Gmail; you come back here. The mode stays off until you switch it on.</Text>
        <Button size="small" data-testid="gmail-google-signin" disabled={!o.client.configured || !valid} isLoading={google.pending} onClick={() => google.go({ mailbox: mailbox.trim().toLowerCase(), ...(name.trim() ? { display_name: name.trim() } : {}) })}>
          Sign in with Google as {valid ? mailbox.trim().toLowerCase() : "…"}
        </Button>
      </div>
      <GmailOAuthSteps o={o} open={!o.client.configured} />
      <details className="rounded-md border border-ui-border-base px-3 py-2 text-[13px]" data-testid="gmail-other-method">
        <summary className="cursor-pointer font-medium">Other method: service account key (domain-wide delegation)</summary>
        <div className="mt-2 flex flex-col gap-2">
          <Text size="xsmall" className="text-ui-fg-subtle">For Workspaces that allow service-account keys. Domain-wide delegation lets the key act as any user of the domain, so Sign in with Google is preferred.</Text>
          <GmailSetupSteps s={s} />
          <div className="flex justify-end">
            <Button size="small" variant="secondary" onClick={onServiceAccount}>Connect with a service account key</Button>
          </div>
        </div>
      </details>
    </div>
  )
}

/**
 * Settings → Customer messages: the support mailbox connected through the Gmail API, by signing in
 * with Google as the mailbox (default) or with a service-account key (domain-wide delegation, under
 * "Other method"). Owner and full-permission staff connect and change it; everyone with settings
 * access sees the status.
 */
export function GmailCard() {
  const { data, error } = useGmail()
  const [connecting, setConnecting] = useState(false)
  const [confirm, setConfirm] = useState<null | "disconnect" | GmailMode>(null)
  const [params, setParams] = useSearchParams()
  const options = useGmailOptions()
  const test = useTestGmail()
  const sync = useSyncGmail()
  const disconnect = useDisconnectGmail()
  const google = useGoogleSignIn()
  const s = data?.gmail
  const back = params.get("gmail_oauth")
  // Back from Google: say how it went (the outcome is kept by the server, not in the URL), once.
  useEffect(() => {
    if (!back || !s) return
    const r = s.oauth.last_result
    if (r) (r.ok ? toast.success(r.message) : toast.error(r.message, { duration: 15_000 }))
    const next = new URLSearchParams(params)
    next.delete("gmail_oauth")
    setParams(next, { replace: true })
  }, [back, s, params, setParams])
  if (error) return <Section title="Support mailbox (Gmail)"><Text size="small" className="p-4 text-ui-fg-error">{errorMessage(error)}</Text></Section>
  if (!s) return null
  const manage = s.can_manage
  const oauth = s.auth_method === "oauth"
  const set = (body: GmailOptions, ok: string) => options.mutate(body, { onSuccess: () => toast.success(ok), onError: (e) => toast.error(errorMessage(e)) })
  const tone = !s.connected ? "neutral" : !s.readable || s.needs_reconnect ? "critical" : s.mode === "full" ? "success" : s.mode === "import" ? "info" : "neutral"
  const importText = !s.import?.started_at ? "Not started (starts when reading is switched on)" : s.import.done_at ? `Done ${formatDate(s.import.done_at)} · ${s.import.threads.toLocaleString()} threads` : `Running · ${s.import.threads.toLocaleString()} threads so far`
  const last = s.oauth.last_result
  const reconnect = () => google.go({ mailbox: s.mailbox ?? s.oauth.default_mailbox })

  return (
    <Section title="Support mailbox (Gmail)" actions={<Badge tone={tone}>{!s.connected ? "Not connected" : !s.readable || s.needs_reconnect ? "Reconnect needed" : MODE_LABEL[s.mode]}</Badge>}>
      <div className="flex flex-col gap-3 p-4">
        <Text size="small" className="text-ui-fg-subtle">
          Connect the store's support mailbox (a Google Workspace mailbox such as hello@) through the Gmail API. Emails to it become conversations in Messages next to contact-form messages, customer replies join the same conversation, and replies sent from Messages go out through Gmail itself, from the mailbox, with the name you choose.
        </Text>
        {last && !last.ok ? (
          <div data-testid="gmail-oauth-result">
            <Banner tone="critical" title="Gmail wasn't connected">{last.message}</Banner>
          </div>
        ) : null}
        {s.connected && s.needs_reconnect ? (
          <div data-testid="gmail-needs-reconnect">
            <Banner tone="critical" title="Reconnect Gmail" action={manage && oauth ? <Button size="small" variant="secondary" isLoading={google.pending} onClick={reconnect}>Reconnect</Button> : undefined}>
              {s.needs_reconnect} Until then nothing is read from Gmail and replies through it are refused.
            </Banner>
          </div>
        ) : null}
        {s.connected && !oauth && !s.readable && <Banner tone="critical" title="The saved key can't be read">The server secret changed. Paste the service account key again.</Banner>}
        {s.connected && !s.needs_reconnect && s.last_test && s.last_test.ok === false && (
          <Banner tone="critical" title="The last connection test failed">
            {s.last_test.message}
          </Banner>
        )}
        {s.connected && !s.needs_reconnect && s.mode !== "off" && s.last_error && (
          <Banner tone="warning" title="The last sync had a problem">
            {s.last_error}
          </Banner>
        )}
      </div>
      {!s.connected && manage ? <SignInPanel s={s} onServiceAccount={() => setConnecting(true)} /> : null}
      {!s.connected && !manage ? <GmailOAuthSteps o={s.oauth} /> : null}
      {s.connected && (
        <DefinitionList
          rows={[
            ["Connected with", oauth ? "Sign in with Google" : "Service account (domain-wide delegation)"],
            ["Mailbox", s.mailbox ?? "—"],
            ["Customers see", s.display_name && s.mailbox ? `${s.display_name} <${s.mailbox}>` : "—"],
            ["Connected by", `${s.connected_by ?? "—"}${s.connected_at ? ` · ${formatDate(s.connected_at)}` : ""}`],
            ...((oauth
              ? [
                  ["Gmail permission", (s.oauth_scopes ?? []).map((x) => x.replace("https://www.googleapis.com/auth/", "")).join(", ") || "—"],
                  ["OAuth client", s.oauth_client_id ?? "—"],
                ]
              : [
                  ["Service account", s.client_email ?? "—"],
                  ["Client ID (for domain-wide delegation)", s.client_id ?? "—"],
                  ["Key", s.key_hint ?? "Saved"],
                ]) as [string, string][]),
            ["Mode", MODE_LABEL[s.mode]],
            ["First import", importText],
            ["Last sync", s.last_sync_at ? `${formatDate(s.last_sync_at)}${s.last_sync_ok_at && s.last_sync_ok_at !== s.last_sync_at ? ` · last good ${formatDate(s.last_sync_ok_at)}` : ""}` : s.mode === "off" ? "Off" : "Not yet (runs every minute)"],
            ["Conversations from email", `${(s.counts?.email_conversations ?? 0).toLocaleString()} · ${(s.counts?.not_customer ?? 0).toLocaleString()} not a customer`],
            ...(s.last_test ? ([["Last connection test", `${s.last_test.ok ? "OK" : "Failed"} · ${formatDate(s.last_test.at)}`]] as [string, string][]) : []),
          ]}
        />
      )}
      {manage && s.connected && s.readable && <Options s={s} busy={options.isPending} onSet={set} onMode={(m) => (m === s.mode ? null : setConfirm(m))} />}
      {manage && s.connected ? (
        <div className="flex flex-col gap-3 border-t border-ui-border-base px-4 py-3">
          {oauth ? <OAuthClientCard o={s.oauth} manage /> : null}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button size="small" variant="secondary" isLoading={test.isPending} onClick={() => test.mutate(undefined, { onSuccess: (r: any) => (r.test.ok ? toast.success(r.test.message) : toast.error(r.test.message)), onError: (e) => toast.error(errorMessage(e)) })}>
              Test connection
            </Button>
            <Button
              size="small"
              variant="secondary"
              isLoading={sync.isPending}
              disabled={s.mode === "off"}
              onClick={() =>
                sync.mutate(undefined, {
                  onSuccess: (r) =>
                    r.result.busy ? toast.info("A sync is already running. Try again in a minute.") : r.result.error ? toast.error(r.result.error) : toast.success(`Synced: ${r.result.added} new email${r.result.added === 1 ? "" : "s"}, ${r.result.created} new conversation${r.result.created === 1 ? "" : "s"}`),
                  onError: (e) => toast.error(errorMessage(e)),
                })
              }
            >
              Sync now
            </Button>
            {oauth ? (
              <Button size="small" variant="secondary" isLoading={google.pending} disabled={!s.oauth.client.configured} onClick={reconnect}>Reconnect</Button>
            ) : (
              <>
                <Button size="small" variant="secondary" onClick={() => setConnecting(true)}>Change key or mailbox</Button>
                <Button size="small" variant="secondary" isLoading={google.pending} disabled={!s.oauth.client.configured} onClick={reconnect} title={s.oauth.client.configured ? undefined : "Add the OAuth client first"}>
                  Sign in with Google instead
                </Button>
              </>
            )}
            <Button size="small" variant="danger" isLoading={disconnect.isPending} onClick={() => setConfirm("disconnect")}>Disconnect</Button>
          </div>
          {!oauth ? (
            <details className="text-[13px]">
              <summary className="cursor-pointer text-ui-fg-subtle">Switch to Sign in with Google (no key, no domain-wide delegation)</summary>
              <div className="mt-2 flex flex-col gap-2">
                <OAuthClientCard o={s.oauth} manage />
                <GmailOAuthSteps o={s.oauth} />
              </div>
            </details>
          ) : null}
        </div>
      ) : null}
      {!manage ? <Text size="xsmall" className="px-4 pb-4 text-ui-fg-subtle">Only store owners and staff with full permissions can connect or change the mailbox.</Text> : null}
      {connecting && <ConnectDrawer s={s} onClose={() => setConnecting(false)} />}
      <Prompt open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>{confirm === "disconnect" ? "Disconnect Gmail?" : `Switch to ${confirm ? MODE_LABEL[confirm as GmailMode].toLowerCase() : ""}?`}</Prompt.Title>
            <Prompt.Description>
              {confirm === "disconnect"
                ? oauth
                  ? "Gmail access is revoked at Google and the saved sign-in is deleted here. Conversations and emails already in Messages stay. Nothing in Gmail changes."
                  : "The key is deleted here. Conversations and emails already in Messages stay. Nothing in Gmail changes. Remove the client ID from domain-wide delegation in the Google Admin console too if it won't be used again."
                : confirm
                  ? `${MODE_HELP[confirm as GmailMode]}${confirm !== "off" ? " The connection is tested first." : ""}`
                  : ""}
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action
              onClick={() =>
                confirm === "disconnect"
                  ? disconnect.mutate(undefined, {
                      onSuccess: (r) =>
                        r.revoked === false ? toast.warning("Gmail disconnected, but Google didn't confirm removing access: remove it at myaccount.google.com → Security → Third-party connections (signed in as the mailbox).", { duration: 15_000 }) : toast.success(r.revoked ? "Gmail disconnected and access removed at Google" : "Gmail disconnected"),
                      onError: (e) => toast.error(errorMessage(e)),
                    })
                  : confirm && set({ mode: confirm as GmailMode }, `Gmail: ${MODE_LABEL[confirm as GmailMode]}`)
              }
            >
              {confirm === "disconnect" ? "Disconnect" : "Switch"}
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </Section>
  )
}

function Options({ s, busy, onSet, onMode }: { s: GmailStatus; busy: boolean; onSet: (b: GmailOptions, ok: string) => void; onMode: (m: GmailMode) => void }) {
  const [name, setName] = useState(s.display_name ?? "")
  const [days, setDays] = useState(String(s.import_days ?? 30))
  const [label, setLabel] = useState(s.label_name ?? "Keel")
  useEffect(() => setName(s.display_name ?? ""), [s.display_name])
  useEffect(() => setDays(String(s.import_days ?? 30)), [s.import_days])
  useEffect(() => setLabel(s.label_name ?? "Keel"), [s.label_name])
  const daysN = Number(days)
  return (
    <div className="flex flex-col gap-4 border-t border-ui-border-base px-4 py-3">
      <Field label="Gmail sync" hint={MODE_HELP[s.mode]}>
        <Select value={s.mode} onValueChange={(v) => onMode(v as GmailMode)} disabled={busy}>
          <Select.Trigger aria-label="Gmail sync"><Select.Value /></Select.Trigger>
          <Select.Content>
            {(Object.keys(MODE_LABEL) as GmailMode[]).map((m) => (
              <Select.Item key={m} value={m}>{MODE_LABEL[m]}</Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Name customers see" hint={`Replies come from “${name || "…"}” <${s.mailbox}>.`}>
          <div className="flex gap-2">
            <Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} aria-label="Name customers see" />
            <Button size="small" variant="secondary" disabled={busy || !name.trim() || name.trim() === s.display_name} onClick={() => onSet({ display_name: name.trim() }, "Name saved")}>Save</Button>
          </div>
        </Field>
        <Field label="First import: emails from the last" hint={s.import?.done_at ? "The first import is done; this only matters if the mailbox changes." : "Days (1–365). Older emails stay in Gmail only."}>
          <div className="flex gap-2">
            <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} aria-label="Import days" />
            <Button size="small" variant="secondary" disabled={busy || !Number.isInteger(daysN) || daysN < 1 || daysN > 365 || daysN === s.import_days} onClick={() => onSet({ import_days: daysN }, "Import window saved")}>Save</Button>
          </div>
        </Field>
      </div>
      <label className="flex items-start gap-2 text-[13px]">
        <Switch checked={!!s.label_enabled} disabled={busy} onCheckedChange={(v) => onSet({ label_enabled: v }, v ? "Imported threads get the label in Gmail" : "No label in Gmail")} />
        <span>
          Label imported threads in Gmail
          <Text size="xsmall" className="text-ui-fg-subtle">Adds a Gmail label (only staff see it) to every thread that is in Messages. Nothing else in Gmail is changed, archived or deleted.</Text>
          {s.label_enabled ? (
            <span className="mt-1 flex gap-2">
              <Input size="small" value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} aria-label="Label name" />
              <Button size="small" variant="secondary" disabled={busy || !label.trim() || label.trim() === s.label_name} onClick={() => onSet({ label_name: label.trim() }, "Label name saved")}>Save</Button>
            </span>
          ) : null}
        </span>
      </label>
      <label className="flex items-start gap-2 text-[13px]">
        <Switch checked={!!s.mark_read} disabled={busy} onCheckedChange={(v) => onSet({ mark_read: v }, v ? "Opening a conversation here marks it read in Gmail" : "Gmail's read state is left alone")} />
        <span>
          Mark read in Gmail when opened here
          <Text size="xsmall" className="text-ui-fg-subtle">Off: Gmail's unread state is left as it is.</Text>
        </span>
      </label>
    </div>
  )
}

function ConnectDrawer({ s, onClose }: { s: GmailStatus; onClose: () => void }) {
  const connect = useConnectGmail()
  const [key, setKey] = useState("")
  const [mailbox, setMailbox] = useState(s.mailbox ?? "")
  const [name, setName] = useState(s.display_name ?? "")
  const k = key.trim()
  const looksJson = k.startsWith("{") && /"private_key"/.test(k) && /"client_email"/.test(k)
  const validMailbox = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/.test(mailbox.trim())
  const submit = () =>
    connect.mutate(
      { key_json: k, mailbox: mailbox.trim().toLowerCase(), ...(name.trim() ? { display_name: name.trim() } : {}) },
      {
        onSuccess: (r) => {
          if (r.test?.ok) toast.success(`Saved. ${r.test.message} Choose the mode on the card.`)
          else toast.warning(`Saved, but the test failed: ${r.test?.message ?? "unknown error"}`, { duration: 15_000 })
          onClose()
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    )
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Connect Gmail with a service account key</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-4 overflow-y-auto">
          <GmailSetupSteps s={s} />
          <Field label="Service account key (JSON)" hint={k && !looksJson ? "Paste the whole .json file: it starts with { and has client_email and private_key." : "Stored encrypted and never shown again."}>
            <Textarea rows={7} value={key} spellCheck={false} autoComplete="off" placeholder='{ "type": "service_account", "client_email": "…", "private_key": "…" }' onChange={(e) => setKey(e.target.value)} aria-label="Service account key" className="font-mono text-[12px]" />
          </Field>
          <Field label="Mailbox" hint={mailbox && !validMailbox ? "Enter an email address." : "The Workspace mailbox to read and send as (a user, not a group)."}>
            <Input value={mailbox} placeholder="hello@yourstore.com" onChange={(e) => setMailbox(e.target.value)} aria-label="Mailbox" />
          </Field>
          <Field label="Name customers see" hint="Shown as the sender of replies, e.g. Tolvane Labs Support.">
            <Input value={name} maxLength={80} placeholder="Tolvane Labs Support" onChange={(e) => setName(e.target.value)} aria-label="Display name" />
          </Field>
          <Text size="xsmall" className="text-ui-fg-subtle">
            Saving tests the connection right away (signs in as the mailbox, reads its profile and one message). The mode stays off until you switch it on, so nothing is imported or sent yet.
          </Text>
        </Drawer.Body>
        <Drawer.Footer>
          <Drawer.Close asChild>
            <Button size="small" variant="secondary">Cancel</Button>
          </Drawer.Close>
          <Button size="small" isLoading={connect.isPending} disabled={!looksJson || !validMailbox} onClick={submit}>
            Save and test
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}
